<template>
  <div class="pa-4">
    <!-- Info alert -->
    <v-alert
      class="mb-4"
      type="info"
      variant="tonal"
      icon="mdi-information">
      Conditions determine when this asset is valid for selection in
      AssetGroups. When a scene references an AssetGroup, the system evaluates
      conditions on each asset to choose the right one.
    </v-alert>

    <!-- Conditions Section -->
    <div class="mb-3">
      <h3 class="text-h6 mb-2">Playback Conditions</h3>
      <v-card variant="outlined">
        <v-card-text>
          <ConditionPicker
            :item="mockAssetItem"
            :itemtype="'asset'"
            :condition_status="conditionStatus"
            @addedcondition="handleConditionsUpdate"
            @deletecondition="handleConditionsUpdate"
            @templateupdate="handleConditionsUpdate" />
        </v-card-text>
      </v-card>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import ConditionPicker from '@/components/Shared/ConditionPicker.vue';
import { $http } from '@/stores/http.js';

const props = defineProps({
  userEditObj: {
    type: Object,
    required: true,
  },
});
const conditionStatus = ref({});
let pollingInterval = null;

// Create a mock item for ConditionPicker that wraps the asset's conditions
const mockAssetItem = computed(() => {
  // Initialize conditions if not present
  if (!props.userEditObj.conditions) {
    props.userEditObj.conditions = {
      play_conditions: [],
      end_conditions: [],
    };
  }

  return {
    conditions: props.userEditObj.conditions,
    uid: props.userEditObj.uid,
    name: props.userEditObj.name,
  };
});

const handleConditionsUpdate = async () => {
  // ConditionPicker modifies conditions in place, so we just need to save
  try {
    await $http('PUT', '/update_asset_conditions', {
      asset_uid: props.userEditObj.uid,
      conditions: props.userEditObj.conditions,
    });
    // Immediately fetch updated status after save
    await fetchConditionStatus();
  } catch (error) {
    console.error('Error saving asset conditions:', error);
  }
};

// Fetch condition status from backend
async function fetchConditionStatus() {
  try {
    const response_raw = await $http(
      'GET',
      `/evaluate_asset_conditions?asset_uid=${props.userEditObj.uid}`
    );
    const response = JSON.parse(response_raw);
    conditionStatus.value = response;
  } catch (error) {
    console.error('Failed to get asset condition status:', error);
    conditionStatus.value = {};
  }
}

// Start polling for condition status when component mounts
onMounted(() => {
  // Only start polling if there are conditions
  if (props.userEditObj.conditions?.play_conditions?.length > 0) {
    // Initial fetch
    fetchConditionStatus();

    // Poll every second
    pollingInterval = setInterval(() => {
      fetchConditionStatus();
    }, 1000);
  }
});

// Stop polling when component unmounts
onUnmounted(() => {
  if (pollingInterval) {
    clearInterval(pollingInterval);
    pollingInterval = null;
  }
});
</script>
